import { useEffect, useId, useState, type FormEvent } from "react";
import { isUnprocessable } from "../../api/errors";
import { usePromoteAgentSession } from "../../api/queries";
import type { PromoteSessionResult } from "../../api/client";
import type { AgentSession } from "../../api/types";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { FormError } from "../../components/FormError";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import "../../components/Form.css";

interface PromoteSessionDialogProps {
  session: AgentSession | null;
  onClose: () => void;
}

/**
 * Pushes a Bonnie session's worktree branch to the developer's own git
 * remote and, optionally, opens a pull request (ADR-0039 P5/P6). No
 * credential or remote URL is ever collected here — the daemon pushes with
 * the developer's own local git/gh auth (see `PromoteSessionInput`'s doc
 * comment) — this dialog only names cosmetic branch/PR metadata.
 */
export function PromoteSessionDialog({ session, onClose }: PromoteSessionDialogProps) {
  const targetBranchId = useId();
  const baseBranchId = useId();
  const openPRId = useId();
  const prTitleId = useId();
  const prBodyId = useId();
  const { pushToast } = useToast();
  const mutation = usePromoteAgentSession();
  const [targetBranch, setTargetBranch] = useState("");
  const [baseBranch, setBaseBranch] = useState("");
  const [openPR, setOpenPR] = useState(false);
  const [prTitle, setPrTitle] = useState("");
  const [prBody, setPrBody] = useState("");
  const [result, setResult] = useState<PromoteSessionResult | null>(null);

  useEffect(() => {
    if (session) {
      setTargetBranch("");
      setBaseBranch("");
      setOpenPR(false);
      setPrTitle("");
      setPrBody("");
      setResult(null);
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  function handleClose() {
    setResult(null);
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session) return;
    mutation.mutate(
      {
        id: session.id,
        input: {
          targetBranch: targetBranch.trim() || undefined,
          baseBranch: openPR ? baseBranch.trim() || undefined : undefined,
          openPR,
          prTitle: openPR ? prTitle.trim() : undefined,
          prBody: openPR ? prBody.trim() || undefined : undefined,
        },
      },
      {
        onSuccess: (res) => {
          setResult(res);
          if (res.pushed) {
            pushToast({ title: `Promoted "${session.label}".`, tone: "success" });
          }
        },
      },
    );
  }

  return (
    <Modal open={session !== null} title="Promote session" onClose={handleClose} initialFocusId={targetBranchId}>
      {session && !result && (
        <form onSubmit={handleSubmit} noValidate>
          <p>
            Push <strong>{session.label}</strong>
            {session.branch ? (
              <>
                {" "}
                (<code>{session.branch}</code>)
              </>
            ) : null}{" "}
            to your git remote. This runs on your own machine with your own git/gh
            credentials — nothing crosses the control plane.
          </p>

          <label className="form-field" htmlFor={targetBranchId}>
            <span>Target branch (optional)</span>
            <input
              id={targetBranchId}
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={targetBranch}
              onChange={(e) => setTargetBranch(e.target.value)}
              maxLength={200}
              placeholder={session.branch || "Leave blank to reuse the session's own branch name"}
            />
          </label>

          <label className="form-field form-field--checkbox" htmlFor={openPRId}>
            <span className="form-field--checkbox__row">
              <input
                id={openPRId}
                type="checkbox"
                checked={openPR}
                onChange={(e) => setOpenPR(e.target.checked)}
              />
              Open a pull request
            </span>
          </label>

          {openPR && (
            <>
              <label className="form-field" htmlFor={baseBranchId}>
                <span>Base branch (optional)</span>
                <input
                  id={baseBranchId}
                  type="text"
                  autoComplete="off"
                  spellCheck={false}
                  value={baseBranch}
                  onChange={(e) => setBaseBranch(e.target.value)}
                  maxLength={200}
                  placeholder="Defaults to the repository's default branch"
                />
              </label>

              <label className="form-field" htmlFor={prTitleId}>
                <span>Pull request title</span>
                <input
                  id={prTitleId}
                  type="text"
                  autoComplete="off"
                  value={prTitle}
                  onChange={(e) => setPrTitle(e.target.value)}
                  required={openPR}
                  maxLength={300}
                />
              </label>

              <label className="form-field" htmlFor={prBodyId}>
                <span>Pull request description (optional)</span>
                <textarea
                  id={prBodyId}
                  rows={4}
                  value={prBody}
                  onChange={(e) => setPrBody(e.target.value)}
                  maxLength={8000}
                />
              </label>
            </>
          )}

          {mutation.isError && mutation.error && (() => {
            const err = mutation.error;
            const message = err.message || "Couldn't promote this session.";
            // isUnprocessable (422) means the request itself was refused
            // (e.g. no worktree to promote, or a missing PR title) — an
            // informational refusal to correct and retry, not a surprise
            // failure.
            return isUnprocessable(err) ? (
              <Banner tone="warning" title="Can't promote yet" className="bonnie-dialog-banner">
                {message}
              </Banner>
            ) : (
              <FormError>{message}</FormError>
            );
          })()}

          <div className="form-actions">
            <Button type="button" variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={mutation.isPending || (openPR && !prTitle.trim())}
            >
              {mutation.isPending ? "Promoting…" : "Promote"}
            </Button>
          </div>
        </form>
      )}

      {session && result && (
        <div>
          {result.pushed ? (
            <Banner tone="info" title="Pushed" className="bonnie-dialog-banner">
              "{session.label}"{result.branch ? <> (<code>{result.branch}</code>)</> : null} was pushed to
              your remote.
            </Banner>
          ) : (
            <Banner tone="danger" title="Push failed" className="bonnie-dialog-banner">
              {result.error || "The push didn't go through."}
            </Banner>
          )}

          {result.pushed && result.prUrl && (
            <p>
              <a href={result.prUrl} target="_blank" rel="noreferrer noopener">
                View pull request
              </a>
            </p>
          )}

          {result.pushed && openPR && !result.prUrl && (
            <Banner tone="warning" title="Pull request wasn't opened" className="bonnie-dialog-banner">
              {result.error || "The branch pushed, but opening a pull request failed."}
            </Banner>
          )}

          <div className="form-actions">
            <Button type="button" variant="primary" onClick={handleClose}>
              Done
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
